import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { bool, object } from "prop-types";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CommentTypes, hashCode } from "@repo/utils";
import { setProjectVersionCommentReadStatus } from "@query";
import { useEstimateEditorStore, useEstimateComments, useEstimateEditorSettings, useCommentLinker } from "@tools";
import { ChatThread } from "@tools/EstimateEditor/components/Comments/components/ChatThread";
import { NavigationSwitch, TooltipIcon } from "@components";
import { ClickAwayListener, Grid, Tooltip } from "@material-ui/core";
import { CheckCircle, Link } from "@material-ui/icons";
import useStyle from "./CommentTile.style"
import classnames from "classnames";

export const CommentTile = observer(({
  group,
  showVersionName,
}) => {
  const { t } = useTranslation();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  const { isTemplate } = useEstimateEditorSettings();
  const { estimateUuid, projectHash } = useParams();
  const classes = useStyle();
  
  const [ focused, setFocused ] = useState(false);
  
  const containerRef = useRef(null);
  
  const { version, name, path, viewedType, thread } = group;
  const { useExternalComments, useInternalComments } = commentStore;
  
  const {
    linkCopied,
    handleCopyLink,
    handleSetAnchor,
    handleJumpToComment,
  } = useCommentLinker({
    version,
    path,
    viewedType,
    hasThread: Boolean(thread),
    // onlyJumpToThread: true,
  });
  
  const versionName = useMemo(() => (
    showVersionName && editorStore.estimateVersions.find(({ key }) => key === version)?.name
  ), [showVersionName]);
  
  const handleRead = async (type) => {
    if(!group[type] || group[type].isRead || isTemplate || !commentStore.commenterData?.uuid)
      return;
    
    group[type].markRead();
    await setProjectVersionCommentReadStatus(group[type].uuid, estimateUuid || projectHash, commentStore.commenterData?.uuid, editorStore.currentVersionKey);
  }
  
  const handleFocus = (e) => {
    if(focused) return;
    setFocused(true);
    handleRead(group.viewedType);
    handleJumpToComment(e, true);
  }
  
  const handleClickAway = (e) => {
    if(!focused)
      return;
    setFocused(false);
    if(!e.target.closest(`[data-comment]`))
      handleSetAnchor();
  }
  
  useLayoutEffect(() => {
    if(focused)
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [focused]);
  
  const handleCommentTypeSwitch = async (e, val) => {
    group.setUsedType(val);
    handleRead(val);
  }
  
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Grid
        ref={containerRef}
        item container xs={12}
        className={classnames(
          classes.root,
          focused && classes.focused
        )}
        onClick={handleFocus}
        data-comment={hashCode(path)}
      >
        <Grid
          item container
          wrap="nowrap"
          alignItems="center"
          className={classnames("hoverRow", classes.componentName)}
        >
          <Tooltip title={t(`views.editor.comments.jump_source`)}>
            <p className={classes.title} onClick={handleJumpToComment}>
              <span className="transparent-2">{ versionName && `${versionName} / ` }</span>
              <span>{ name || t("common.unnamed_element") }</span>
            </p>
          </Tooltip>
          {
            group.isUnread &&
            <span className={classes.newComment}>{t("common.new")}</span>
          }
          {
            Boolean(thread) &&
            <TooltipIcon
              size="small"
              color="primary"
              onClick={handleCopyLink}
              className="hoverIcon ml-1"
              icon={linkCopied ? <CheckCircle className="color-success" /> : <Link />}
            >
              {t(`views.editor.comments.link.${linkCopied ? "copied" : "copy"}`)}
            </TooltipIcon>
          }
        </Grid>
        {
          useInternalComments && useExternalComments &&
          <Grid item container className="mb-3">
            <NavigationSwitch
              value={viewedType}
              values={[
                { value: CommentTypes.INTERNAL, label: t("views.editor.comments.internal"), ariaLabel: "internal comments", className: "nav-internal" },
                { value: CommentTypes.EXTERNAL, label: t("views.editor.comments.external"), ariaLabel: "external comments", className: "nav-external" },
              ]}
              aria-label="comment type switch"
              onChange={handleCommentTypeSwitch}
              role="navigation"
              className="w-half"
              lighter
            />
          </Grid>
        }
        
        <ChatThread
          group={group}
          useMaxHeight={false}
          maxDisplayReplies={focused ? undefined : 2}
          showNewMessageEditor={focused}
        />
      </Grid>
    </ClickAwayListener>
  )
});

CommentTile.proptypes = {
  group: object.isRequired,
  showVersionName: bool
}